'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["aria-label", "children", "className", "depth", "divider", "headingText", "_rootComponent", "value"],
  _excluded2 = ["children", "_useNativeButton"];
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { isValidElement, cloneElement, Children, Fragment, Component } from 'react';
import devLogger from 'react-utils/devLogger';
import getComponentName from 'react-utils/getComponentName';
import classNames from 'classnames';
import I18n from 'I18n';
import { warnIfFragment } from '../utils/devWarnings';
import { isChildActive, containsActiveChild } from './utils/navUtils';
import { BASE_FONT_SIZE } from 'HubStyleTokens/sizes';
import UINavListItem from './UINavListItem';
import UIButton from '../button/UIButton';
import UILink from '../link/UILink';
import HR from '../elements/HR';
import H5 from '../elements/headings/H5';
import { FORM_TOGGLE_BORDER_COLOR } from 'HubStyleTokens/theme';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
let hasPingedNewRelic = false;
const Ul = styled.ul.withConfig({
  displayName: "UINavList__Ul",
  componentId: "sc-1h054uq-0"
})(["list-style:none;padding-left:0;margin-bottom:4px;line-height:16px;& .private-nav__wrapper{.private-nav-item{font-size:", ";padding-bottom:7px;padding-top:7px;padding-left:24px;line-height:24px;}.private-folder-nav-item{padding-left:20px;}}", ""], BASE_FONT_SIZE, ({
  depth
}) => depth === 0 ? `margin-bottom: 0;` : `
    .private-nav__wrapper .private-nav-item {
      padding-left: ${20 * (depth + 1)}px;
    }

    .private-nav__wrapper {
      ul {
        margin-bottom: 0;
      }
    }
    `);
const Nav = styled.nav.withConfig({
  displayName: "UINavList__Nav",
  componentId: "sc-1h054uq-1"
})(["", " .private-nav__wrapper{margin-bottom:0;}&:first-of-type{margin-top:0;}"], ({
  divider
}) => {
  if (divider) {
    return `margin-top: 13px;`;
  }
  return ` margin-top: 33px;`;
});
const StyledH5 = styled(props => /*#__PURE__*/_jsx(H5, Object.assign({}, props))).withConfig({
  displayName: "UINavList__StyledH5",
  componentId: "sc-1h054uq-2"
})(["&&{margin-bottom:9px;line-height:22px;}"]);
const StyledHR = styled(HR).withConfig({
  displayName: "UINavList__StyledHR",
  componentId: "sc-1h054uq-3"
})(["&&{background-color:", ";margin:13px 0 20px 0;}"], FORM_TOGGLE_BORDER_COLOR);
class UINavList extends Component {
  constructor(...args) {
    super(...args);
    this.rerenderFromRoot = children => {
      if (children == null) return null;
      const {
        depth,
        value,
        _rootComponent,
        _transformChildProps
      } = this.props;
      const Root = _rootComponent || UINavList;
      const props = {
        depth: depth + 1,
        value,
        _rootComponent,
        _transformChildProps
      };
      return /*#__PURE__*/_jsx(Root, Object.assign({}, props, {
        children: children
      }));
    };
  }
  render() {
    const _this$props = this.props,
      {
        'aria-label': ariaLabel,
        children,
        className,
        depth,
        divider,
        headingText,
        value
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const classes = classNames(className, 'private-nav__wrapper');
    const isTopLevelNav = depth === 0;
    const computedAriaLabel = ariaLabel || headingText || I18n.text('ui.UISideNav.navigationLandmark');
    if (isTopLevelNav && computedAriaLabel === I18n.text('ui.UISideNav.navigationLandmark')) {
      devLogger.warn({
        message: `UISideNav: In order for screenreaders to understand the content of your UISideNav, label it with a unique, descriptive aria-label. You do not need to include the word "navigation" in your label as it will be added to the end automatically.`,
        key: `UISideNav`
      });
    }
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [divider && /*#__PURE__*/_jsx(StyledHR, {}), /*#__PURE__*/_jsxs(Nav, Object.assign({}, rest, {
        className: classes,
        "aria-label": isTopLevelNav ? computedAriaLabel : null,
        as: isTopLevelNav ? 'nav' : 'div',
        divider: divider,
        depth: depth,
        children: [headingText && /*#__PURE__*/_jsx(StyledH5, {
          children: headingText
        }), /*#__PURE__*/_jsx(Ul, {
          depth: depth,
          children: Children.map(children, child => {
            if (! /*#__PURE__*/isValidElement(child)) {
              return child;
            }
            const defaultOpen = child && child.props && child.props.defaultOpen;
            if (child.type === HR || child.type === H5) {
              return child; // to prevent a console warning as a result of UISideNav having H5 or HR as children
            } else if (
            // Converts buttons, links, and a tags to `UINavListItem`s
            child.type === UIButton || child.type === UILink || child.type === 'a') {
              const _child$props = child.props,
                otherProps = _objectWithoutPropertiesLoose(_child$props, _excluded2);
              devLogger.warn({
                message: `UISideNav: Using a UIButton, UILink, or an <a> as a child is deprecated. ` + `Use a \`UISideNavItem\` instead.`,
                key: `UISideNav`
              });
              if (window.newrelic && !hasPingedNewRelic) {
                window.newrelic.addPageAction('deprecated-nav-child', {
                  type: typeof child.type === 'string' ? child.type : getComponentName(child.type)
                });
                hasPingedNewRelic = true;
              }
              return /*#__PURE__*/_jsx(UINavListItem, Object.assign({
                "aria-label": ariaLabel,
                active: isChildActive(child, value),
                defaultOpen: defaultOpen || containsActiveChild(child, value),
                href: child.props.href,
                onClick: child.props.onClick,
                title: child.props.children,
                value: undefined,
                _rootRenderer: this.rerenderFromRoot
              }, otherProps));
            }
            warnIfFragment(child, UINavList.displayName);
            return /*#__PURE__*/cloneElement(child, {
              active: isChildActive(child, value),
              defaultOpen: defaultOpen || containsActiveChild(child, value),
              value: undefined,
              _rootRenderer: this.rerenderFromRoot
            });
          })
        })]
      }))]
    });
  }
}
UINavList.displayName = 'UINavList';
UINavList.propTypes = {
  'aria-label': PropTypes.string,
  children: PropTypes.node,
  depth: PropTypes.number,
  divider: PropTypes.bool,
  headingText: PropTypes.node,
  value: PropTypes.node,
  _rootComponent: PropTypes.func,
  _transformChildProps: PropTypes.func
};
UINavList.defaultProps = {
  depth: 0,
  value: null,
  _transformChildProps: () => ({})
};
export default UINavList;